import {
    useEffect,
    useState
} from "react";
import {
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useGlobalState
} from "../../../context";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import {
    type LoginRequestType
} from "../../../services/restAPI/actions/login/types";
import type RootStackParamList from "../../../types/navigation";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    LockKeyholeIcon,
    MailIcon
} from "lucide-react-native";
import md5 from "md5";
import GMKonyaLogo from "../../../assets/images/gmkonyalogo.png";
import GMKonyaLogoDark from "../../../assets/images/gmkonyalogodark.png";
import OneSignalService from "../../../services/OneSignalService";
import {
    RESTService
} from "../../../services/restAPI";
import {
    InstallAppBanner
} from "../../../components";

const Login = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute();

    const {
        activeTheme,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const lineDynamicStyle = {
        borderColor: colors.content.text.low,
        borderBottomWidth: borders.line / 2,
        marginVertical: spaces.spacingMd
    };

    const textDynamicStyle = {
        marginHorizontal: spaces.spacingLg
    };

    const [globalState,
        setGlobalState] = useGlobalState();
    const [phoneNumber,
        setPhoneNumber] = useState("");
    const [loading,
        setLoading] = useState(false);
    const [password,
        setPassword] = useState("");
    const [email,
        setEmail] = useState("");
    const [emailOrPhoneInput,
        setEmailOrPhoneInput] = useState("");

    const [isValid,
        setIsValid] = useState(false);

    useEffect(() => {
        setIsValid(Boolean((email || phoneNumber) && password));
    }, [
        email,
        phoneNumber,
        password
    ]);

    const loginHandler = () => {
        if(!(email || phoneNumber)){
            NCoreUIKitToast.open({
                title: localize("error"),
                subTitle: localize("please-enter-email-or-phone"),
                type: "danger"
            });
            return;
        }

        if(!password){
            NCoreUIKitToast.open({
                title: localize("error"),
                subTitle: localize("please-enter-password"),
                type: "danger"
            });
            return;
        }

        setLoading(true);
        const loginParams: LoginRequestType = {
            password: md5(password),
            ...(email && {
                mail: email.trim()
            }),
            ...(!email && {
                phoneNumber: phoneNumber.replace(/\D/g, "").trim()
            })
        };

        RESTService.action("Login", loginParams)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    const userInfo = res.payload.userInfo;

                    setGlobalState({
                        ...globalState,
                        isAppReady: true,
                        isGuest: false,
                        user: {
                            profilePhotoURL: userInfo.profilePhoto,
                            companyTitle: userInfo.companyTitle,
                            companyName: userInfo.companyName,
                            phoneNumber: userInfo.phoneNumber,
                            firstName: userInfo.firstName,
                            lastName: userInfo.lastName,
                            fullName: userInfo.fullName,
                            userType: userInfo.userType,
                            company: userInfo.company,
                            userID: userInfo.userID,
                            mail: userInfo.mail
                        }
                    });

                    OneSignalService.login(userInfo.userID);
                    OneSignalService.setTags({
                        userID: userInfo.userID
                    });

                    setTimeout(() => {
                        const routeParams = route.params as {
                            redirectRoute?: string;
                            redirectParams?: object;
                        } | undefined;

                        if (routeParams?.redirectRoute) {
                            navigation.replace("App", {
                                screen: routeParams.redirectRoute,
                                params: routeParams.redirectParams
                            } as never);
                        } else {
                            navigation.replace("App", {
                                screen: "MainTabs"
                            } as never);
                        }
                    }, 300);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    return <PageContainer
        style={[
            stylesheet.container,
            {
                padding: spaces.spacingLg
            }
        ]}
    >
        <InstallAppBanner />
        <Image
            resizeMode="contain"
            source={activeTheme === "dark" ? GMKonyaLogoDark : GMKonyaLogo}
            style={[
                stylesheet.logo,
                {
                    marginTop: spaces.spacingXl * 2,
                    marginBottom: spaces.spacingLg
                }
            ]}
        />
        <View
            style={stylesheet.contentContainer}
        >
            <TextInput
                spreadBehaviour="stretch"
                placeholder={localize("enter-email-or-phone")}
                icon={() => <MailIcon
                    color={colors.content.icon.high}
                    size={15}
                />}
                type={!emailOrPhoneInput ? "default" : (email || phoneNumber) ? "default" : "danger"}
                hintText={(!email && !phoneNumber && emailOrPhoneInput) ? (localize("invalid-email-or-phone")) : undefined}
                style={{
                    marginBottom: spaces.spacingLg
                }}
                onChangeText={(text) => {
                    setEmailOrPhoneInput(text);
                    const isMail = text.includes("@");
                    const isPhone = /^\d+$/.test(text);

                    if (isMail) {
                        setEmail(text);
                        setPhoneNumber("");
                    } else if (isPhone) {
                        setPhoneNumber(text);
                        setEmail("");
                    } else {
                        setEmail("");
                        setPhoneNumber("");
                    }
                }}
            />
            <TextInput
                spreadBehaviour="stretch"
                variant="hidden"
                placeholder={localize("enter-password")}
                icon={() => <LockKeyholeIcon
                    color={colors.content.icon.high}
                    size={15}
                />}
                style={{
                    marginBottom: spaces.spacingXl
                }}
                onChangeText={(text) => {
                    setPassword(text);
                }}
            />
            <Button
                customBorderColor="transparent"
                spreadBehaviour="stretch"
                isLoading={loading}
                isDisabled={!isValid}
                title={localize("login")}
                size="large"
                onPress={() => {
                    loginHandler();
                }}
                style={{
                    marginBottom: spaces.spacingSm
                }}
            />
            <Button
                variant="ghost"
                spreadBehaviour="stretch"
                title={localize("forgot-password")}
                size="large"
                customTextColor={colors.content.text.high}
                onPress={() => {
                    navigation.navigate("Auth", {
                        screen: "ResetPassword"
                    } as never);
                }}
            />
            <View
                style={[
                    stylesheet.orlineContainer,
                    {
                        marginBottom: spaces.spacingMd * 1.5
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.line,
                        lineDynamicStyle
                    ]}
                />
                <Text
                    customColor={colors.content.text.low}
                    variant="bodyLargeSize"
                    style={[
                        textDynamicStyle
                    ]}
                >
                    {localize("or")}
                </Text>
                <View
                    style={[
                        stylesheet.line,
                        lineDynamicStyle
                    ]}
                />
            </View>
            <Button
                variant="outline"
                spreadBehaviour="stretch"
                title={localize("continue-without-sign-up")}
                size="large"
                customTextColor={colors.content.text.high}
                customBorderColor="default"
                style={{
                    backgroundColor: colors.content.container.default
                }}
                onPress={() => {
                    setGlobalState({
                        ...globalState,
                        isGuest: true,
                        isAppReady: true
                    });
                    setTimeout(() => {
                        const routeParams = route.params as {
                            redirectRoute?: string;
                            redirectParams?: object;
                        } | undefined;

                        if (routeParams?.redirectRoute === "ApplyForm") {
                            navigation.navigate("Guest", {
                                screen: "GuestApplyForm",
                                params: routeParams.redirectParams
                            } as never);
                        } else {
                            navigation.navigate("Guest" as never);
                        }
                    }, 300);
                }}
            />
        </View>
    </PageContainer>;
};
export default Login;
